import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isToday as isTodayFn, isSameDay, getHours } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Workout } from '../types';
import { CalendarWeek, CalendarHour } from '../types/calendar';

// Константы для календаря
export const WORKING_HOURS = {
  start: 6,
  end: 22
};

export const HOURS_IN_DAY = Array.from(
  { length: WORKING_HOURS.end - WORKING_HOURS.start + 1 },
  (_, i) => WORKING_HOURS.start + i
);

// Генерация недели календаря
export const generateCalendarWeek = (date: Date): CalendarWeek => {
  const startDate = startOfWeek(date, { weekStartsOn: 1 }); // Понедельник
  const endDate = endOfWeek(date, { weekStartsOn: 1 }); // Воскресенье
  
  const days = eachDayOfInterval({ start: startDate, end: endDate }).map(date => {
    const dayOfWeek = format(date, 'EEEE', { locale: ru });
    const dayNumber = date.getDate();
    const isTodayFlag = isTodayFn(date);
    
    // Создаем часы для дня
    const hours: CalendarHour[] = HOURS_IN_DAY.map(hour => ({
      hour,
      workouts: [],
      isHighlighted: false,
      isCurrentHour: false
    }));
    
    return {
      date,
      dayOfWeek,
      dayNumber,
      isToday: isTodayFlag,
      hours
    };
  });
  
  return {
    startDate,
    endDate,
    days
  };
};

// Генерация одного дня календаря (для мобильной версии)
export const generateCalendarDay = (date: Date): CalendarWeek => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  
  const dayOfWeek = format(normalizedDate, 'EEEE', { locale: ru });
  const dayNumber = normalizedDate.getDate();
  const isTodayFlag = isTodayFn(normalizedDate);
  
  // Создаем часы для дня
  const hours: CalendarHour[] = HOURS_IN_DAY.map(hour => ({
    hour,
    workouts: [],
    isHighlighted: false,
    isCurrentHour: false
  }));
  
  const day = {
    date: normalizedDate,
    dayOfWeek,
    dayNumber,
    isToday: isTodayFlag,
    hours
  };
  
  return {
    startDate: normalizedDate,
    endDate: normalizedDate,
    days: [day]
  };
};

// Группировка тренировок по дням и часам
export const groupWorkoutsByDayAndHour = (workouts: Workout[], week: CalendarWeek): CalendarWeek => {
  const updatedWeek = { ...week };
  
  // Убираем дубликаты тренировок по ID
  const uniqueWorkouts = workouts.filter((workout, index, self) => 
    index === self.findIndex(w => w.id === workout.id)
  );
  
  uniqueWorkouts.forEach(workout => {
    const workoutDate = new Date(workout.date);
    // Получаем час в локальном времени
    const workoutHour = workoutDate.getHours();
    
    // Находим день в неделе
    const dayIndex = updatedWeek.days.findIndex(day => 
      isSameDay(day.date, workoutDate)
    );
    
    if (dayIndex !== -1) {
      // Находим час в дне
      const hourIndex = updatedWeek.days[dayIndex].hours.findIndex(hour => 
        hour.hour === workoutHour
      );
      
      if (hourIndex !== -1) {
        // Проверяем, что тренировка еще не добавлена в этот час
        const existingWorkout = updatedWeek.days[dayIndex].hours[hourIndex].workouts.find(
          w => w.id === workout.id
        );
        
        if (!existingWorkout) {
          // Добавляем тренировку к часу
          updatedWeek.days[dayIndex].hours[hourIndex].workouts.push(workout);
          updatedWeek.days[dayIndex].hours[hourIndex].isHighlighted = true;
        }
      }
    }
  });
  
  return updatedWeek;
};

// Навигация по неделям
export const getPreviousWeek = (currentWeek: CalendarWeek): CalendarWeek => {
  const previousWeekStart = subWeeks(currentWeek.startDate, 1);
  return generateCalendarWeek(previousWeekStart);
};

export const getNextWeek = (currentWeek: CalendarWeek): CalendarWeek => {
  const nextWeekStart = addWeeks(currentWeek.startDate, 1);
  return generateCalendarWeek(nextWeekStart);
};

// Получение текущей недели
export const getCurrentWeek = (): CalendarWeek => {
  return generateCalendarWeek(new Date());
};

// Форматирование времени
export const formatTime = (hour: number): string => {
  return `${hour < 10 ? '0' : ''}${hour}:00`;
};

// Проверка, является ли час текущим
export const isCurrentHour = (hour: number): boolean => {
  const now = new Date();
  return now.getHours() === hour;
};

// Обновление текущего часа в неделе
export const updateCurrentHour = (week: CalendarWeek): CalendarWeek => {
  const updatedWeek = { ...week };
  const now = new Date();
  const currentHour = now.getHours();
  
  updatedWeek.days.forEach(day => {
    day.hours.forEach(hour => {
      hour.isCurrentHour = hour.hour === currentHour;
    });
  });
  
  return updatedWeek;
}; 